import { useRef, useState, useEffect } from 'react';
import { Animated, Modal, PanResponder, Pressable, ScrollView, StyleSheet, Text, ActivityIndicator, View, useWindowDimensions } from 'react-native';
import { useTranslation } from "react-i18next";
import { useTheme } from '@react-navigation/native';
import { showMessage } from 'react-native-flash-message';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomLabel from '../../components/CustomLabel/Index';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import { useShifts } from '../../hooks/UseShifts';
import { useAccountContext } from '../../hooks/useAccountContext';
import { createCommonStyles } from '../../styles/commonStyles';


const mondayOf = (date) => {
	const monday = new Date(date);
	monday.setHours(12, 0, 0, 0);
	monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7);
	return monday;
};

const dateOnly = (value) => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	date.setHours(12, 0, 0, 0);
	return date;
};

const formatTime = (value) => value ? String(value).slice(0, 5) : '';
const formatDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const getAssignmentKey = (employeeId, shiftDate) => `${employeeId}:${String(shiftDate).slice(0, 10)}`;

const ShiftBox = ({ shift, onDrop, onDragStart, onDragEnd, onEdit, isSaving, noBreaksText, styles }) => {
	const pan = useRef(new Animated.ValueXY()).current;
	const callbacks = useRef({ onDrop, onDragStart, onDragEnd });
	callbacks.current = { onDrop, onDragStart, onDragEnd };
	const responder = useRef(PanResponder.create({
		onMoveShouldSetPanResponder: (_, gesture) => Math.abs(gesture.dx) > 5 || Math.abs(gesture.dy) > 5,
		onPanResponderGrant: () => callbacks.current.onDragStart(),
		onPanResponderMove: (_, gesture) => pan.setValue({ x: gesture.dx, y: gesture.dy }),
		onPanResponderRelease: (_, gesture) => {
			callbacks.current.onDrop(gesture.moveX, gesture.moveY);
			pan.setValue({ x: 0, y: 0 });
			callbacks.current.onDragEnd();
		},
		onPanResponderTerminate: () => {
			pan.setValue({ x: 0, y: 0 });
			callbacks.current.onDragEnd();
		},
	})).current;

	return (
		<Animated.View {...responder.panHandlers} style={[styles.shiftBox, shift?.colorCode && { backgroundColor: shift.colorCode }, pan.getLayout()]}>
			<Text numberOfLines={1} style={styles.shiftName}>{typeof shift === 'string' ? shift : shift.tempName}</Text>
			{typeof shift !== 'string' && (
				<>
					<Text numberOfLines={1} style={styles.shiftTime}>{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</Text>
					{shift.breaks?.length > 0 ? (
						<Text numberOfLines={2} style={styles.shiftBreaks}>
							Breaks: {shift.breaks.map((shiftBreak) => `${formatTime(shiftBreak.breakStart)}-${formatTime(shiftBreak.breakEnd)}`).join(', ')}
						</Text>
					) : (
						<Text numberOfLines={1} style={styles.shiftBreaks}>{noBreaksText}</Text>
					)}
				</>
			)}
			{(shift.assignmentId || shift.isPending) && (
				<Pressable
					style={styles.assignmentEditButton}
					onPress={onEdit}
					disabled={isSaving}
					accessibilityRole="button"
					accessibilityLabel="Edit shift assignment"
				>
					<Ionicons name="pencil-outline" size={12} color={styles.shiftName.color} />
				</Pressable>
			)}
		</Animated.View>
	);
};

const ShiftsRosterScreen = ({ schedulePeriodId, company, dept, onPendingChange }) => {
	const { colors } = useTheme();
	const { width } = useWindowDimensions();
	const common = createCommonStyles(colors);
	const { t } = useTranslation();
	const { account } = useAccountContext();
	const employeeId = account?.employeeId;
	const { getAuthEmployeesWithLeavesFY, getShiftTemplates, getShiftTemplateBreaks, getShiftAssignments, saveShiftAssignment, deleteShiftAssignment } = useShifts();
	const [authEmplWithLeaves, setAuthEmplWithLeaves] = useState(null);
	const employeeWidth = width < 600 ? 136 : 190;
	const dayWidth = Math.max(132, (width - employeeWidth - 32) / 7);
	const styles = makeStyles(colors, employeeWidth, dayWidth);
	const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
	const [searchText, setSearchText] = useState('');
	const [weeklyRosters, setWeeklyRosters] = useState({});
	const [draggingRow, setDraggingRow] = useState(null);
	const [shiftTemplates, setShiftTemplates] = useState([]);
	const [savedAssignments, setSavedAssignments] = useState([]);
	const [pendingAssignmentChanges, setPendingAssignmentChanges] = useState({});
	const [pendingAssignmentDeletions, setPendingAssignmentDeletions] = useState({});
	const [isSavingAssignment, setIsSavingAssignment] = useState(false);
	const [selectionTarget, setSelectionTarget] = useState(null);
	const daysScrollRef = useRef(null);
	const scrollOffset = useRef(0);
	const weekKey = weekStart.toDateString();
	const employeesById = new Map();
	(authEmplWithLeaves ?? []).forEach((record) => {
		if (record?.employeeID == null) return;
		const employeeId = String(record.employeeID);
		if (!employeesById.has(employeeId)) {
			employeesById.set(employeeId, {
				employeeID: employeeId,
				name: record.name ?? '',
				leaves: [],
			});
		}
		if (record.startdate != null && record.enddate != null) {
			employeesById.get(employeeId).leaves.push(record);
		}
	});

	const employees = [...employeesById.values()];
	const employeesByIdForGrid = new Map(employees.map((employee) => [employee.employeeID, employee]));
	const employeeRoster = employees.map((employee) => [
		String(employee.employeeID),
		employee.name ?? '',
		...Array(7).fill(null),
	]);
	const assignmentsRoster = employeeRoster.map((row) => [...row]);
	savedAssignments.forEach((assignment) => {
		const assignmentDate = dateOnly(assignment.shiftDate);
		if (!assignmentDate || mondayOf(assignmentDate).toDateString() !== weekKey) return;

		const rowIndex = assignmentsRoster.findIndex(([employeeId]) => employeeId === String(assignment.employeeId));
		if (rowIndex === -1) return;

		const dayIndex = (assignmentDate.getDay() + 6) % 7;
		assignmentsRoster[rowIndex][dayIndex + 2] = {
			...(assignment.shiftTemplate ?? {}),
			assignmentId: assignment.id,
			employeeId: assignment.employeeId,
			shiftTemplateId: assignment.shiftTemplateId,
			schedulePeriodId: assignment.schedulePeriodId,
			shiftDate: assignment.shiftDate,
			actualStart: assignment.actualStart,
			actualEnd: assignment.actualEnd,
			status: assignment.status,
			notes: assignment.notes,
		};
	});
	const currentRoster = weeklyRosters[weekKey] ?? assignmentsRoster;
	const normalizedSearch = searchText.trim().toLocaleLowerCase();
	const visibleRoster = normalizedSearch
		? currentRoster.filter(([employeeId, employeeName]) =>
			`${employeeName} ${employeeId}`.toLocaleLowerCase().includes(normalizedSearch)
		)
		: currentRoster;
	const days = Array.from({ length: 7 }, (_, index) => {
		const day = new Date(weekStart);
		day.setDate(weekStart.getDate() + index);
		return day;
	});
	const activeShiftTemplates = shiftTemplates.filter((template) => template.isActive !== false);
	const hasPendingChanges = Object.keys(pendingAssignmentChanges).length > 0
		|| Object.keys(pendingAssignmentDeletions).length > 0;

	useEffect(() => {
		onPendingChange?.(hasPendingChanges);
	}, [hasPendingChanges]);

	useEffect(() => () => onPendingChange?.(false), []);

	useEffect(() => {
		if (employeeId == null) return;
		let isCurrent = true;
		getAuthEmployeesWithLeavesFY(employeeId, company, dept).then((res) => {
			if (isCurrent) setAuthEmplWithLeaves(res);
		}).catch((error) => {
			if (isCurrent) console.log(error);
		});
		return () => { isCurrent = false; };
	}, [employeeId, company, dept]);

	useEffect(() => {
		let isCurrent = true;
		getShiftTemplates().then((res) => {
			if (isCurrent) setShiftTemplates(Array.isArray(res) ? res : []);
		}).catch((error) => {
			if (isCurrent) console.log(error);
		});

		setSavedAssignments([]);
		setPendingAssignmentChanges({});
		setPendingAssignmentDeletions({});
		setWeeklyRosters({});
		if (!schedulePeriodId) return () => { isCurrent = false; };

		getShiftAssignments(schedulePeriodId).then((assignments) => {
			if (isCurrent) setSavedAssignments(Array.isArray(assignments) ? assignments : []);
		}).catch((error) => {
			if (isCurrent) console.error(error);
		});

		return () => { isCurrent = false; };
	}, [schedulePeriodId]);

	const moveWeek = (weeks) => {
		setWeekStart((current) => {
			const next = new Date(current);
			next.setDate(current.getDate() + weeks * 7);
			return next;
		});
	};

	const moveShift = (fromEmployeeId, fromDay, dropX, dropY) => {
		daysScrollRef.current?.measureInWindow((x, y, visibleWidth) => {
			const visibleToRow = Math.floor((dropY - y - 66) / 66);
			const toDay = Math.floor((dropX - x + scrollOffset.current) / dayWidth);
			if (dropX < x || dropX >= x + visibleWidth || visibleToRow < 0 || visibleToRow >= visibleRoster.length || toDay < 0 || toDay >= 7) return;
			const fromRow = currentRoster.findIndex(([employeeId]) => employeeId === fromEmployeeId);
			const toEmployeeId = visibleRoster[visibleToRow][0];
			const toRow = currentRoster.findIndex(([employeeId]) => employeeId === toEmployeeId);
			if (fromRow === -1 || toRow === -1) return;
			if (fromRow === toRow && fromDay === toDay) return;

			setWeeklyRosters((previous) => {
				const updated = (previous[weekKey] ?? employeeRoster).map((row) => [...row]);
				[updated[fromRow][fromDay + 2], updated[toRow][toDay + 2]] =
					[updated[toRow][toDay + 2], updated[fromRow][fromDay + 2]];
				return { ...previous, [weekKey]: updated };
			});
		});
	};

	const assignShiftTemplate = async (template) => {
		if (!selectionTarget || !schedulePeriodId) return;
		const rowIndex = currentRoster.findIndex(([employeeId]) => employeeId === selectionTarget.employeeId);
		if (rowIndex === -1) return;
		let breaks = [];
		try {
			const result = await getShiftTemplateBreaks(template.id);
			breaks = Array.isArray(result) ? result : [];
		} catch (error) {
			console.error(error);
		}

		const existing = selectionTarget.assignment;
		const payload = {
			id: existing?.assignmentId,
			employeeId: selectionTarget.employeeId,
			shiftTemplateId: template.id,
			schedulePeriodId,
			company,
			dept,
			shiftDate: selectionTarget.shiftDate,
			actualStart: existing?.actualStart ?? null,
			actualEnd: existing?.actualEnd ?? null,
			status: existing?.status || 'Scheduled',
			notes: existing?.notes ?? null,
		};
		const assignment = {
			...payload,
			assignmentId: existing?.assignmentId,
			isPending: true,
			shiftTemplate: { ...template, breaks },
		};
		const assignmentKey = getAssignmentKey(payload.employeeId, payload.shiftDate);
		setPendingAssignmentChanges((previous) => ({ ...previous, [assignmentKey]: assignment }));
		if (existing?.assignmentId) {
			setPendingAssignmentDeletions((previous) => {
				const next = { ...previous };
				delete next[existing.assignmentId];
				return next;
			});
		}
		setWeeklyRosters((previous) => {
			const updated = (previous[weekKey] ?? assignmentsRoster).map((row) => [...row]);
			updated[rowIndex][selectionTarget.dayIndex + 2] = {
				...template,
				breaks,
				...assignment,
			};
			return { ...previous, [weekKey]: updated };
		});
		setSelectionTarget(null);
	};

	const deleteSelectedAssignment = () => {
		const assignmentId = selectionTarget?.assignment?.assignmentId;
		if (!selectionTarget?.assignment) return;

		if (!assignmentId) {
			const assignmentKey = getAssignmentKey(selectionTarget.employeeId, selectionTarget.shiftDate);
			setPendingAssignmentChanges((previous) => {
				const next = { ...previous };
				delete next[assignmentKey];
				return next;
			});
			setWeeklyRosters((previous) => {
				const updated = (previous[weekKey] ?? assignmentsRoster).map((row) => [...row]);
				const rowIndex = updated.findIndex(([employeeId]) => employeeId === selectionTarget.employeeId);
				if (rowIndex !== -1) updated[rowIndex][selectionTarget.dayIndex + 2] = null;
				return { ...previous, [weekKey]: updated };
			});
			setSelectionTarget(null);
			return;
		}

		const assignmentKey = getAssignmentKey(selectionTarget.employeeId, selectionTarget.shiftDate);
		setPendingAssignmentChanges((previous) => {
			const next = { ...previous };
			delete next[assignmentKey];
			return next;
		});
		setPendingAssignmentDeletions((previous) => ({ ...previous, [assignmentId]: selectionTarget.assignment }));
		setWeeklyRosters((previous) => {
			const updated = (previous[weekKey] ?? assignmentsRoster).map((row) => [...row]);
			const rowIndex = updated.findIndex(([employeeId]) => employeeId === selectionTarget.employeeId);
			if (rowIndex !== -1) updated[rowIndex][selectionTarget.dayIndex + 2] = null;
			return { ...previous, [weekKey]: updated };
		});
		setSelectionTarget(null);
	};

	const savePendingAssignments = async () => {
		if (!hasPendingChanges || isSavingAssignment) return;

		setIsSavingAssignment(true);
		try {
			for (const assignmentId of Object.keys(pendingAssignmentDeletions)) {
				const success = await deleteShiftAssignment(assignmentId);
				if (!success) {
					showMessage({ message: t('common:deleteFailed'), type: 'danger', duration: 3000 });
					return;
				}
				setPendingAssignmentDeletions((previous) => {
					const next = { ...previous };
					delete next[assignmentId];
					return next;
				});
				setSavedAssignments((previous) => previous.filter((assignment) => assignment.id !== assignmentId));
			}

			for (const [assignmentKey, assignment] of Object.entries(pendingAssignmentChanges)) {
				const { shiftTemplate, assignmentId, isPending, ...payload } = assignment;
				const result = await saveShiftAssignment({ ...payload, id: assignmentId });
				if (!result?.id) {
					showMessage({ message: t('common:saveError'), type: 'danger', duration: 3000 });
					return;
				}

				const savedAssignment = {
					...payload,
					id: result.id,
					assignmentId: result.id,
					shiftTemplate,
				};
				setSavedAssignments((previous) => [
					...previous.filter((saved) => saved.id !== assignmentId && getAssignmentKey(saved.employeeId, saved.shiftDate) !== assignmentKey),
					savedAssignment,
				]);
				setWeeklyRosters((previous) => {
					const updated = (previous[weekKey] ?? assignmentsRoster).map((row) => [...row]);
					const rowIndex = updated.findIndex(([employeeId]) => employeeId === String(assignment.employeeId));
					const dayIndex = days.findIndex((day) => formatDateKey(day) === String(assignment.shiftDate).slice(0, 10));
					if (rowIndex !== -1 && dayIndex !== -1) {
						updated[rowIndex][dayIndex + 2] = {
							...assignment.shiftTemplate,
							...savedAssignment,
							assignmentId: result.id,
							isPending: false,
						};
					}
					return { ...previous, [weekKey]: updated };
				});
				setPendingAssignmentChanges((previous) => {
					const next = { ...previous };
					delete next[assignmentKey];
					return next;
				});
			}

			setPendingAssignmentChanges({});
			setPendingAssignmentDeletions({});
			setWeeklyRosters({});
			showMessage({ message: t('common:allOperationsSuccMess'), type: 'success', duration: 3000 });
		} finally {
			setIsSavingAssignment(false);
		}
	};

	return (
		<View style={[common.container, styles.container]}>
			<View style={styles.toolbar}>
				<CustomLabel
					value={t('navigate:shiftRoster')}
					type='MINORTITLE'
					fontSize={20}
				/>
				{/* week selector  */}
				<View style={styles.weekControls}>
					{hasPendingChanges && (
						<CustomButton
							text={t('common:save')}
							iconName="save-outline"
							onPress={savePendingAssignments}
							disabled={isSavingAssignment}
							show="BUTTON_SAVE"
							type="BUTTON_SAVE"
							isGradient
							textTop={0}
							sz={15}
						/>
					)}
					<Pressable onPress={() => moveWeek(-1)} disabled={isSavingAssignment} style={styles.arrow} accessibilityRole="button" accessibilityLabel="Previous week">
						<Ionicons name="chevron-back" size={20} color={colors.text_primary} />
					</Pressable>
					<Text style={styles.weekLabel}>
						{days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {days[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
					</Text>
					<Pressable onPress={() => moveWeek(1)} disabled={isSavingAssignment} style={styles.arrow} accessibilityRole="button" accessibilityLabel="Next week">
						<Ionicons name="chevron-forward" size={20} color={colors.text_primary} />
					</Pressable>
				</View>
			</View>
			{/* search bar */}
			<View style={styles.searchBar}>
				<CustomInput
					iconName="search-outline"
					iconSize={18}
					show="SEARCH"
					type='SEARCH'
					value={searchText || ""}
					setValue={setSearchText}
					placeholder={`${t('common:search')}...`}
				/>
				{searchText.length > 0 && (
					<Pressable onPress={() => setSearchText('')} accessibilityRole="button" accessibilityLabel={t('common:clear')}>
						<Ionicons name="close-circle" size={18} color={colors.text_muted} />
					</Pressable>
				)}
			</View>
			<View style={styles.roster}>
				<ScrollView style={styles.grid} contentContainerStyle={styles.gridContent}>
					<View style={styles.employeeColumn}>
						<View style={[styles.employeeCell, styles.headerCell]}>
							<CustomLabel
								type='AVAILABLEUSED_SECONDARY'
								value={t('common:employee')}
								fontSize={15}
							/>
						</View>
						{visibleRoster.map(([employeeId, employeeName]) => (
							<View key={employeeId} style={styles.employeeCell}>
								<CustomLabel
									multiline
									type='AVAILABLEUSED_SECONDARY'
									value={employeeName}
									fontSize={15} />
							</View>
						))}
					</View>
					<ScrollView
						ref={daysScrollRef}
						horizontal
						showsHorizontalScrollIndicator
						style={styles.daysScroll}
						onScroll={(event) => { scrollOffset.current = event.nativeEvent.contentOffset.x; }}
						scrollEventThrottle={16}
					>
						<View style={styles.daysGrid}>
							<View style={styles.dayRow}>
								{days.map((day) => (
									<View key={day.toDateString()} style={[styles.dayCell, styles.headerCell]}>
										<CustomLabel
											type='AVAILABLEUSED_SECONDARY'
											value={day.toLocaleDateString(undefined, { weekday: 'short' })}
											fontSize={15} />
										<CustomLabel
											type='AVAILABLEUSED_SECONDARY'
											value={day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
											fontSize={14} />
									</View>
								))}
							</View>
							{visibleRoster.map(([employeeId, employeeName, ...shifts], rowIndex) => (
								<View key={employeeId} style={[styles.dayRow, draggingRow === rowIndex && styles.draggingRow]}>
									{shifts.map((shift, index) => {
										const day = days[index];
										const dayTime = day.setHours(12, 0, 0, 0);
										const leaves = (employeesByIdForGrid.get(employeeId)?.leaves ?? []).filter((leave) => {
											const start = dateOnly(leave.startdate);
											const end = dateOnly(leave.enddate);
											return start && end && dayTime >= start.getTime() && dayTime <= end.getTime();
										});
										return (
											<View key={index} style={styles.dayCell}>
												{leaves.length > 0 ? (
													<View style={[styles.leaveBox, { backgroundColor: leaves[0].colour || colors.buttons_primary }]}>
														<CustomLabel
															type='AVAILABLEUSED_SECONDARY'
															value={[...new Set(leaves.map((leave) => leave.description).filter(Boolean))].join(', ')}
															fontSize={14} />
													</View>
												) : shift ? (
													<ShiftBox
														shift={shift}
														styles={styles}
														noBreaksText={t('common:noBreaks')}
														isSaving={isSavingAssignment}
														onEdit={() => setSelectionTarget({ employeeId, employeeName, dayIndex: index, shiftDate: formatDateKey(day), assignment: shift })}
														onDragStart={() => setDraggingRow(rowIndex)}
														onDragEnd={() => setDraggingRow(null)}
														onDrop={(x, y) => moveShift(employeeId, index, x, y)}
													/>
												) : (
													<Pressable
														style={styles.addShiftButton}
														disabled={isSavingAssignment}
														onPress={() => setSelectionTarget({ employeeId, employeeName, dayIndex: index, shiftDate: formatDateKey(day) })}
														accessibilityRole="button"
														accessibilityLabel="Add shift"
													>
														<Ionicons name="add" size={22} color={colors.text_muted} />
													</Pressable>
												)}
											</View>
										)
									})}
								</View>
							))}
						</View>
					</ScrollView>
				</ScrollView>
			</View>
			<Modal
				visible={selectionTarget !== null}
				transparent
				animationType="fade"
				onRequestClose={() => setSelectionTarget(null)}
			>
				<View style={styles.modalOverlay}>
					<Pressable style={StyleSheet.absoluteFill} onPress={() => setSelectionTarget(null)} />
					<View style={styles.modalContent}>
						<View style={styles.modalHeader}>
							<View style={styles.assignmentModalTitle}>
								<CustomLabel value={selectionTarget?.employeeName ?? t('common:shiftType')} type="MINORTITLE" fontSize={18} />
								<Text style={styles.templateTime}>{selectionTarget?.shiftDate}</Text>
							</View>
							<View style={styles.modalActions}>
								{selectionTarget?.assignment && (
									<Pressable onPress={deleteSelectedAssignment} accessibilityRole="button" accessibilityLabel={t('common:delete')}>
										<Ionicons name="trash-outline" size={20} color={colors.text_primary} />
									</Pressable>
								)}
								<Pressable onPress={() => setSelectionTarget(null)} accessibilityRole="button" accessibilityLabel={t('common:cancel')}>
									<Ionicons name="close" size={22} color={colors.text_primary} />
								</Pressable>
							</View>
						</View>
						<ScrollView>
							{activeShiftTemplates.map((template) => (
								<Pressable
									key={template.id}
									disabled={isSavingAssignment}
									style={[styles.templateOption, selectionTarget?.assignment?.shiftTemplateId === template.id && styles.selectedTemplateOption]}
									onPress={() => assignShiftTemplate(template)}
								>
									<View style={[styles.templateColor, { backgroundColor: template.colorCode || colors.buttons_primary }]} />
									<View style={styles.templateDetails}>
										<Text style={styles.templateName}>{template.tempName}</Text>
										<Text style={styles.templateTime}>{template.startTime} - {template.endTime}</Text>
									</View>
								</Pressable>
							))}
							{isSavingAssignment && <ActivityIndicator animating color={colors.green} />}
							{activeShiftTemplates.length === 0 && (
								<Text style={styles.emptyTemplates}>{t('common:noDataFound')}</Text>
							)}
						</ScrollView>
					</View>
				</View>
			</Modal>
		</View>
	);
};

const makeStyles = (colors, employeeWidth, dayWidth) => StyleSheet.create({
	container: {
		backgroundColor: colors.background,
		padding: 12,
	},
	toolbar: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		gap: 12,
		marginBottom: 16,
	},
	title: {
		fontSize: 20,
		fontWeight: '600',
		color: colors.text_primary,
	},
	weekControls: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	searchBar: {
		minHeight: 42,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		paddingHorizontal: 10,
		marginBottom: 12,
		backgroundColor: colors.background_white,
		borderWidth: 1,
		borderColor: colors.border_card,
		borderRadius: 4,
	},
	searchInput: {
		flex: 1,
		paddingVertical: 8,
		color: colors.text_primary,
		fontSize: 14,
	},
	arrow: {
		width: 36,
		height: 36,
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: 1,
		borderColor: colors.border_card,
		borderRadius: 4,
	},
	weekLabel: {
		minWidth: 150,
		textAlign: 'center',
		color: colors.text_primary,
		fontSize: 14,
		fontWeight: '600',
	},
	roster: {
		flex: 1,
	},
	grid: {
		flex: 1,
		backgroundColor: colors.background_white,
		borderWidth: 1,
		borderColor: colors.border_card,
	},
	gridContent: {
		flexDirection: 'row',
	},
	employeeColumn: {
		width: employeeWidth,
		borderRightWidth: 1,
		borderColor: colors.border_card,
	},
	daysScroll: {
		flex: 1,
	},
	daysGrid: {
		width: dayWidth * 7,
	},
	dayRow: {
		flexDirection: 'row',
	},
	draggingRow: {
		zIndex: 1,
	},
	employeeCell: {
		height: 66,
		justifyContent: 'center',
		paddingHorizontal: 12,
		borderBottomWidth: 1,
		borderColor: colors.border_card,
	},
	dayCell: {
		width: dayWidth,
		height: 66,
		justifyContent: 'center',
		alignItems: 'center',
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: colors.border_card,
	},
	headerCell: {
		backgroundColor: colors.background,
	},
	shiftName: {
		fontSize: 11,
		fontWeight: '600',
		color: colors.text_primary,
		textAlign: 'center',
	},
	shiftTime: {
		fontSize: 10,
		color: colors.text_primary,
	},
	shiftBreaks: {
		fontSize: 9,
		color: colors.text_primary,
		textAlign: 'center',
	},
	shiftBox: {
		width: dayWidth - 12,
		height: 60,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: 4,
		backgroundColor: colors.background,
		borderColor: colors.border_card,
		borderWidth: 1,
		borderRadius: 4,
	},
	assignmentEditButton: {
		position: 'absolute',
		right: 2,
		top: 2,
		padding: 2,
	},
	addShiftButton: {
		width: dayWidth - 12,
		height: 44,
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: 1,
		borderStyle: 'dashed',
		borderColor: colors.border_card,
		borderRadius: 4,
	},
	modalOverlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
		backgroundColor: 'rgba(0, 0, 0, 0.45)',
	},
	modalContent: {
		width: '100%',
		maxWidth: 480,
		maxHeight: '80%',
		padding: 16,
		backgroundColor: colors.background_white,
		borderRadius: 6,
	},
	modalHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 12,
	},
	assignmentModalTitle: {
		flex: 1,
	},
	modalActions: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	templateOption: {
		flexDirection: 'row',
		alignItems: 'center',
		minHeight: 58,
		paddingHorizontal: 10,
		borderTopWidth: StyleSheet.hairlineWidth,
		borderColor: colors.border_card,
	},
	selectedTemplateOption: {
		borderLeftWidth: 3,
		borderLeftColor: colors.green,
	},
	templateColor: {
		width: 10,
		height: 36,
		marginRight: 12,
		borderRadius: 2,
	},
	templateDetails: {
		flex: 1,
	},
	templateName: {
		fontSize: 14,
		fontWeight: '600',
		color: colors.text_primary,
	},
	templateTime: {
		marginTop: 3,
		fontSize: 12,
		color: colors.text_muted,
	},
	emptyTemplates: {
		paddingVertical: 20,
		textAlign: 'center',
		color: colors.text_muted,
	},
	leaveBox: {
		width: dayWidth - 12,
		minHeight: 36,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: 4,
		paddingVertical: 3,
		borderRadius: 4,
	},
	leaveText: {
		fontSize: 11,
		fontWeight: '600',
		color: colors.background_white,
		textAlign: 'center',
	},
});

export default ShiftsRosterScreen;
